import { useState } from "react";
import { motion } from "framer-motion";

const MAN = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/3d734dad3d74353992573cf4ae65b9edf63fb0eb331a0e81ee254e6b66b3b92b.jpeg";
const WOMAN = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/64df4c648c7812305c69559b5c938c099f11c49d3c79dc25e2e3f96327b8f424.jpeg";

// facial landmark-like points (% of the mockup box)
const PTS = [
  [42, 20], [50, 18], [58, 20], [34, 27], [66, 27],
  [37, 30], [44, 29], [56, 29], [63, 30], [40, 34],
  [46, 34], [54, 34], [60, 34], [50, 33], [50, 38],
  [45, 42], [50, 43], [55, 42], [33, 38], [67, 38],
  [35, 46], [65, 46], [43, 49], [50, 50], [57, 49],
  [38, 53], [50, 57], [62, 53], [30, 32], [70, 32],
];
const LINKS = [
  [0, 1], [1, 2], [0, 3], [2, 4], [3, 5], [5, 6], [6, 13], [13, 7], [7, 8], [8, 4],
  [9, 10], [10, 14], [14, 11], [11, 12], [13, 14], [14, 16], [15, 16], [16, 17],
  [18, 15], [19, 17], [20, 22], [22, 23], [23, 24], [24, 21], [25, 26], [26, 27],
  [20, 25], [21, 27], [28, 5], [29, 8], [3, 18], [4, 19], [18, 20], [19, 21],
];

export function FacialMappingMockup() {
  const [gender, setGender] = useState("homem");
  const src = gender === "homem" ? MAN : WOMAN;

  return (
    <div className="relative mx-auto w-full max-w-[380px]">
      {/* gender toggle */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 flex rounded-full bg-black/70 border border-[#F5C74B]/30 backdrop-blur p-1">
        {[["homem", "Homem"], ["mulher", "Mulher"]].map(([id, label]) => (
          <button key={id} data-testid={`mockup-gender-${id}`} onClick={() => setGender(id)}
            className={`px-5 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-colors ${gender === id ? "bg-[#F5C74B] text-black" : "text-white/70 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* phone frame */}
      <div className="relative rounded-[2.5rem] border border-white/10 bg-[#0b0b0d] p-2.5 shadow-[0_30px_80px_-20px_rgba(245,199,75,0.25)]">
        <div className="relative overflow-hidden rounded-[2rem] aspect-[9/16] bg-black">
          <motion.img
            key={gender}
            src={src}
            alt={`Mapeamento facial ${gender}`}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />

          {/* top status + title */}
          <div className="absolute top-0 inset-x-0 px-4 pt-3 z-10">
            <div className="flex justify-between text-[10px] text-white/70 font-medium">
              <span>1:21</span>
              <span className="tracking-widest">◉ ⬡ ▢</span>
            </div>
            <p className="mt-2 text-[11px] tracking-[0.25em] text-[#F5C74B] font-semibold">ANÁLISE FACIAL AVANÇADA</p>
          </div>

          {/* mapping overlay */}
          <svg key={`svg-${gender}`} viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-10">
            {LINKS.map(([a, b], i) => (
              <motion.line
                key={i}
                x1={PTS[a][0]} y1={PTS[a][1]} x2={PTS[b][0]} y2={PTS[b][1]}
                stroke="#F5C74B" strokeWidth="0.18" strokeOpacity="0.55"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.55 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.02 }}
              />
            ))}
            {PTS.map(([x, y], i) => (
              <motion.circle
                key={i} cx={x} cy={y} r="0.55" fill="#F5C74B"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 0.8], scale: 1 }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.03 }}
              />
            ))}
          </svg>

          {/* scan line */}
          <motion.div
            key={`scan-${gender}`}
            className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#F5C74B] to-transparent z-10"
            style={{ boxShadow: "0 0 14px 2px rgba(245,199,75,0.7)" }}
            initial={{ top: "10%" }}
            animate={{ top: ["10%", "62%", "10%"] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* corner brackets */}
          {[["top-16 left-4", "border-t-2 border-l-2"], ["top-16 right-4", "border-t-2 border-r-2"],
            ["bottom-24 left-4", "border-b-2 border-l-2"], ["bottom-24 right-4", "border-b-2 border-r-2"]].map(([pos, b], i) => (
            <div key={i} className={`absolute ${pos} w-6 h-6 ${b} border-[#F5C74B]/60 rounded-sm z-10`} />
          ))}

          {/* bottom result card */}
          <div className="absolute bottom-3 inset-x-3 z-10">
            <div className="rounded-xl bg-black/75 backdrop-blur border border-white/10 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-[11px] font-semibold text-white tracking-wide">
                  <span className="w-2 h-2 rounded-full bg-[#38d15a] animate-pulse" /> MAPEAMENTO CONCLUÍDO
                </span>
                <span className="text-[11px] text-white/60">Precisão: 99.8%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full rounded-full bg-[#F5C74B]"
                  initial={{ width: "0%" }} animate={{ width: "99.8%" }} transition={{ duration: 1.6, delay: 0.4 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
